import React, { useState } from 'react';
import { useTranslations } from 'next-intl';
import type {
  Market,
  LanguageCode,
  MarketCode,
} from '@silvertours/common-landingpages-view';
import { MarketPicker } from '@silvertours/front-features';
import { analytics, useSiteInfo } from '@silvertours/front-entities';
import {
  BilligerMietwagenLanguage,
  CarigamiLanguage,
} from 'common/landingpages/i18n/src/features/NavigationLegacy';

type Props = {
  onSubmit: () => void;
};

export const LocalizedMarketPicker = ({ onSubmit }: Props) => {
  const t = useTranslations('features.navigationLegacy.navigation.header');
  const markets = t.raw('marketPicker.markets') as Partial<
    Record<BilligerMietwagenLanguage | CarigamiLanguage, Market>
  >;
  const header = t('marketPicker.header');
  const save = t('marketPicker.save');
  const close = t('marketPicker.close');
  const language = t('marketPicker.language');
  const country = t('marketPicker.country');

  const {
    settings: { market, validMarkets },
  } = useSiteInfo();

  const initialMarket =
    Object.values(markets).find(m => m.country.code === market) ||
    Object.values(markets)[0];

  const filteredMarkets = Object.values(markets).filter(m =>
    validMarkets.includes(m.country.code),
  );

  const [selectedMarket, selectMarket] = useState<Market>(initialMarket);
  const [selectedLanguage, selectLanguage] = useState<LanguageCode>(
    selectedMarket.language.code,
  );

  const handleLanguageChange = (
    languageCode: LanguageCode,
    marketCode: MarketCode,
  ) => {
    selectMarket(
      Object.values(markets).find(m => m.language.code === languageCode) ||
        Object.values(markets)[0],
    );
    selectLanguage(languageCode);
    analytics.gtm.trackLanguageChangeAttempted(marketCode, languageCode);
  };

  const handleMarketChange = (newMarket: Market) => {
    selectMarket(newMarket);
  };

  const handleSubmit = () => {
    onSubmit();
  };

  return (
    <MarketPicker
      header={header}
      languageLabel={language}
      countryLabel={country}
      saveButtonText={save}
      close={close}
      markets={filteredMarkets}
      selectedLanguage={selectedLanguage}
      selectedMarket={selectedMarket}
      onMarketChange={handleMarketChange}
      onLanguageChange={handleLanguageChange}
      onSubmit={handleSubmit}
      disableLanguageChange={false}
      disableMarketChange
      disableSubmit
    />
  );
};
